import { useState } from "react";
import ModalComponent from "../../../../components/modals/ModalFormComponent";
import Label from "../../../../components/form_fields/Label";
import { SubmitHandler, useForm } from "react-hook-form";
import {
	useCreateBonusGroup,
	useUpdateBonusGroup,
} from "../../../../services/bonus-group.service";
import {
	EMPLOYMENT_PERIOD_UNIT,
	PAYMENT_FREQUENCY,
} from "../../../../models/bonus.dto";
import {
	FREQUENCY,
	IAddEditTableForm,
	compareDateFields,
	enumToStringConverter,
	formatRate,
} from "../../../../helpers/appHelpers";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import FormDescriptionInput from "../../../../components/form_fields/FormDescriptionInput";
import MoneyInput from "../../../../components/form_fields/MoneyInput";
import OrangeButton from "../../../../components/buttons/OrangeButton";
import { useManageTableWithSearchParamsData } from "../../../../helpers/useManageTable";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import { CompanyCurrencyConfigDto } from "../../../../models/company-currency.dto";
import FormCodeInput from "../../../../components/form_fields/FormCodeInput";

interface IFormInputs {
	code: string;
	name: string;
	description: string;
	periodStartDate: string;
	periodEndDate: string;
	minEmploymentPeriod?: number;
	employmentPeriodUnit?: EMPLOYMENT_PERIOD_UNIT;
	paymentFrequency?: PAYMENT_FREQUENCY;
	paymentInstance?: number;
	minValue: number;
	maxValue?: number;
	prorate: string;
	active: string;
	currencyId: number;
}

type BonusGroupModalFormProps = IAddEditTableForm & {
	companyCurrencies: CompanyCurrencyConfigDto[];
	reloadTableData?: () => void;
};

const BonusGroupModalForm = ({
	open,
	setOpen,
	title,
	isEditForm,
	tableAPIRef,
	companyCurrencies,
	reloadTableData,
}: BonusGroupModalFormProps) => {
	const { currentCompany } = useCompanyStore();
	const { formDataValues } = useEditFormStore();
	const [uploading, setUploading] = useState(false);

	const {
		register,
		watch,
		handleSubmit,
		setValue,
		reset,
		formState: { errors },
	} = useForm<IFormInputs>({
		defaultValues: {
			name: isEditForm ? formDataValues?.name : "",
			code: isEditForm ? formDataValues?.code : "",
			description: isEditForm ? formDataValues?.description : "",
			periodStartDate: isEditForm
				? formDataValues?.periodStartDate.slice(0, 10)
				: "",
			periodEndDate: isEditForm
				? formDataValues?.periodEndDate.slice(0, 10)
				: "",
			minEmploymentPeriod: isEditForm
				? formDataValues?.minEmploymentPeriod || undefined
				: undefined,
			employmentPeriodUnit: isEditForm
				? formDataValues?.employmentPeriodUnit
				: undefined,
			paymentFrequency: isEditForm
				? formDataValues?.paymentFrequency
				: undefined,
			paymentInstance: isEditForm ? formDataValues?.paymentInstance : undefined,
			minValue: isEditForm ? formDataValues?.minValue : "",
			maxValue: isEditForm ? formDataValues?.maxValue : undefined,
			currencyId: isEditForm ? formDataValues?.currencyId : "",
			prorate: isEditForm ? (formDataValues?.prorate ? "yes" : "no") : "",
			active: isEditForm ? (formDataValues?.active ? "yes" : "no") : "",
		},
	});

	const minEmploymentPeriod = watch("minEmploymentPeriod");
	const employmentPeriodUnit = watch("employmentPeriodUnit");

	const { mutate: createBonusGroup } = useCreateBonusGroup();
	const { mutate: updateBonusGroup } = useUpdateBonusGroup();
	const { addNewTableData } = useManageTableWithSearchParamsData();

	const onSubmit: SubmitHandler<IFormInputs> = (data: any) => {
		setUploading(true);

		data.prorate = data.prorate === "yes" ? true : false;
		data.active = data.active === "yes" ? true : false;

		if (isEditForm) {
			updateBonusGroup(
				{
					payload: data,
					companyId: currentCompany?.id as number,
					id: formDataValues?.id,
				},
				{
					onSuccess: (updatedData) => {
						try {
							tableAPIRef?.current.updateRows([updatedData.data]);
						} catch (error) {
							console.error("Error updating table rows:", error);
							reloadTableData?.();
						}
						setOpen(false);
						reset();
					},
					onSettled: () => setUploading(false),
				},
			);
		} else {
			createBonusGroup(
				{ companyId: currentCompany?.id as number, payload: data },
				{
					onSuccess: () => {
						addNewTableData();
						setOpen(false);
						reset();
					},
					onSettled: () => setUploading(false),
				},
			);
		}
	};

	const handleClose = () => {
		setOpen(false);
		reset();
	};

	return (
		<ModalComponent open={open} title={title} handleClose={handleClose}>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="grid grid-cols-2 gap-x-4 gap-y-6">
					<div className="w-full">
						<Label title="Name" for="name" />
						<input
							defaultValue={isEditForm ? formDataValues?.name : ""}
							placeholder={"Enter name"}
							type="text"
							{...register("name", {
								required: "Enter name here",
							})}
							id="name"
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
								errors.name
									? " border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
						/>
						{errors.name && (
							<p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Unique Code" for="code" />
						<FormCodeInput
							register={register}
							attributes={{
								className: `w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
									errors.code
										? " border border-red-500 focus:border-red-500 focus:outline-red-500"
										: "border-none"
								}`,
							}}
						/>
						{errors.code && (
							<p className="text-red-500 text-sm mt-1">{errors.code.message}</p>
						)}
					</div>
					<div className="w-full col-span-2">
						<Label title="Description" for="description" optional />
						<FormDescriptionInput
							required={false}
							register={register}
							attributes={{
								defaultValue: isEditForm ? formDataValues?.description : "",
								className: `w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
									errors.description
										? " border border-red-500 focus:border-red-500 focus:outline-red-500"
										: "border-none"
								}`,
							}}
						/>
						{errors.description && (
							<p className="text-red-500 text-sm mt-1">
								{errors.description.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Currency" for="currencyId" />
						<select
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm mb-2 ${
								errors.currencyId
									? " border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
							{...register("currencyId", {
								required: "Select currency here",
								valueAsNumber: true,
							})}
						>
							<option value="">Select Currency</option>
							{companyCurrencies?.map((item) => (
								<option key={item?.id} value={item?.id}>
									{item?.currency?.name} ({item?.currency?.code})
								</option>
							))}
						</select>
						{errors.currencyId && (
							<p className="text-red-500 text-sm mt-1">
								{errors.currencyId.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Payment Frequency" for="paymentFrequency" optional />
						<select
							defaultValue={isEditForm ? formDataValues?.paymentFrequency : ""}
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm mb-2 ${
								errors.paymentFrequency
									? " border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
							{...register("paymentFrequency")}
						>
							<option value="">Select Frequency</option>
							{FREQUENCY.map((item, index) => (
								<option key={index} value={item}>
									{enumToStringConverter(item)}
								</option>
							))}
						</select>
						{errors.paymentFrequency && (
							<p className="text-red-500 text-sm mt-1">
								{errors.paymentFrequency.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Payment Instance" for="paymentInstance" optional />
						<input
							defaultValue={isEditForm ? formDataValues?.paymentInstance : ""}
							placeholder={"Enter payment instance"}
							type="number"
							{...register("paymentInstance", {
								valueAsNumber: true,
							})}
							id="paymentInstance"
							min={1}
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
								errors.paymentInstance
									? " border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
						/>
						{errors.paymentInstance && (
							<p className="text-red-500 text-sm mt-1">
								{errors.paymentInstance.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Minimum Value" for="minValue" />
						<MoneyInput
							attributes={{
								id: "minValue",
								placeholder: "0",
							}}
							register={register}
							name="minValue"
							setValue={setValue}
							defaultValue={isEditForm ? formDataValues?.minValue : undefined}
							required="Enter minimum value here"
							error={errors.minValue}
						/>
						{errors.minValue && (
							<p className="text-red-500 text-sm mt-1">
								{errors.minValue.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Maximum Value" for="maxValue" optional />
						<MoneyInput
							attributes={{
								id: "maxValue",
								placeholder: "0",
							}}
							register={register}
							name="maxValue"
							setValue={setValue}
							defaultValue={isEditForm ? formDataValues?.maxValue : undefined}
							required={false}
							error={errors.maxValue}
						/>
						{errors.maxValue && (
							<p className="text-red-500 text-sm mt-1">
								{errors.maxValue.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Start Date of Period" for="periodStartDate" />
						<input
							defaultValue={
								isEditForm ? formDataValues?.periodStartDate.slice(0, 10) : ""
							}
							placeholder={"mm/dd/yyyy"}
							type="date"
							{...register("periodStartDate", {
								required: "Enter start date here",
							})}
							id="periodStartDate"
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
								errors.periodStartDate
									? " border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
						/>
						{errors.periodStartDate && (
							<p className="text-red-500 text-sm mt-1">
								{errors.periodStartDate.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="End Date of Period" for="periodEndDate" />
						<input
							defaultValue={
								isEditForm ? formDataValues?.periodEndDate.slice(0, 10) : ""
							}
							placeholder={"mm/dd/yyyy"}
							type="date"
							{...register("periodEndDate", {
								required: "Enter end date here",
								validate: (value) =>
									compareDateFields(watch("periodStartDate"), value),
							})}
							id="periodEndDate"
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
								errors.periodEndDate
									? " border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
						/>
						{errors.periodEndDate && (
							<p className="text-red-500 text-sm mt-1">
								{errors.periodEndDate.message}
							</p>
						)}
					</div>
					<div className="w-full col-span-2">
						<Label
							title="Minimum Employment Period"
							for="minEmploymentPeriod"
							optional
						/>
						<div className="flex items-center text-sm">
							<input
								placeholder={"0"}
								type="number"
								{...register("minEmploymentPeriod", {
									required: employmentPeriodUnit
										? "Enter minimum employment period or clear unit"
										: false,
									valueAsNumber: true,
								})}
								id="minEmploymentPeriod"
								min={1}
								className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 ${
									errors.minEmploymentPeriod
										? " border border-red-500 focus:border-red-500 focus:outline-red-500"
										: "border-none"
								}`}
							/>
							<select
								{...register("employmentPeriodUnit", {
									required: minEmploymentPeriod
										? "Select unit or clear minimum employment period"
										: false,
								})}
								className={`dark ${
									errors.employmentPeriodUnit
										? " border border-red-500 focus:border-red-500 focus:outline-red-500"
										: "border-none"
								}`}
							>
								<option value="">Select Unit</option>
								<option value="DAY">Day</option>
								<option value="MONTH">Month</option>
								<option value="YEAR">Year</option>
							</select>
						</div>
						{(errors.minEmploymentPeriod || errors.employmentPeriodUnit) && (
							<p className="text-red-500 text-sm mt-1">
								{errors.minEmploymentPeriod?.message ||
									errors.employmentPeriodUnit?.message}
							</p>
						)}
					</div>
					<div className="col-span-2">
						<div className="flex space-x-12 text-base">
							<h3>Prorate</h3>
							<div className="flex space-x-3 items-center">
								<input
									{...register("prorate")}
									defaultChecked={
										isEditForm && formDataValues?.prorate === true
									}
									type="radio"
									value={"yes"}
									id="prorate"
								/>
								<label htmlFor="prorate">Yes</label>
							</div>
							<div className="flex space-x-3 items-center">
								<input
									{...register("prorate")}
									defaultChecked={
										isEditForm && formDataValues?.prorate === false
									}
									type="radio"
									value={"no"}
									id="no-prorate"
								/>
								<label htmlFor="no-prorate">No</label>
							</div>
						</div>
						{errors.prorate && (
							<p className="text-red-500 text-sm mt-1">
								{errors.prorate.message}
							</p>
						)}
					</div>
					<div className="col-span-2">
						<div className="flex space-x-12 text-base">
							<h3>Active</h3>
							<div className="flex space-x-3 items-center">
								<input
									{...register("active")}
									defaultChecked={isEditForm && formDataValues?.active === true}
									type="radio"
									value={"yes"}
									id="active"
								/>
								<label htmlFor="active">Yes</label>
							</div>
							<div className="flex space-x-3 items-center">
								<input
									{...register("active")}
									defaultChecked={
										isEditForm && formDataValues?.active === false
									}
									type="radio"
									value={"no"}
									id="inactive"
								/>
								<label htmlFor="inactive">No</label>
							</div>
						</div>
						{errors.active && (
							<p className="text-red-500 text-sm mt-1">
								{errors.active.message}
							</p>
						)}
					</div>
				</div>

				<div className="w-full flex justify-end mt-8">
					<OrangeButton
						type="submit"
						title={
							uploading
								? "Uploading..."
								: isEditForm
									? "Update Bonus Group"
									: "Add Bonus Group"
						}
						className="bg-primary-mango text-white h-14 px-8 rounded-lg text-sm"
						disabled={uploading}
					/>
				</div>
			</form>
		</ModalComponent>
	);
};

export default BonusGroupModalForm;
